import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import {
  Button,
  CloseButton,
  Description,
  Dropdown,
  Input,
  Label,
  ListBox,
  Modal,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@heroui/react'
import { Icon } from '../ui/icons'
import { NoteHistory, type NoteHistoryHandle } from '../ui/NoteHistory'
import { useStaffText } from '../ui/StaffText'
import { requestKindLabel } from '../lib/deskStatus'
import { MAX_PLANNED_SERVICES, whyNotPlanned } from '../lib/plan'
import {
  deliveredName,
  entryLabel,
  entryTime,
  filterDelivered,
  needsOutcome,
  offeredOutcomes,
} from '../lib/delivered'
import { nameIn, type StaffLanguage } from '../lib/staffText'
import {
  COUNTER_DEFAULT_PLACEMENT,
  COUNTER_PLACEMENTS,
  placementBody,
  placementLabel,
  sendRequest,
  targetLabel,
  type Colleague,
  type Placement,
  type SendTarget,
} from '../lib/transfer'
import { ProfilePicker } from './counterOpen'
import type {
  BreakReason,
  CounterView,
  DeliveredEntry,
  DeliveredService,
  ParkScope,
  QueueView,
  RequestKind,
  Service,
  Ticket,
  WorkProfile,
} from '../lib/types'

// The Counter's questions: a confirmation or a picker is a HeroUI Modal; a form
// about the customer at the desk opens inline under the action dock.

/** Where a transfer places the customer, as the server is sent it. */
export type Placed = { placement?: Placement; delaySec?: number }

/** A destination chosen in the Transfer panel. */
type Dest = { kind: SendTarget; id: string }

/** The break kinds, in the order offered. Close is not one: Close counter asks
 *  for it when the branch wants a decision. */
export const BREAK_KINDS: RequestKind[] = ['lunch', 'short_break', 'task']

/** A counter dialog: HeroUI's Modal, open while it is mounted. Escape and the backdrop close it. */
export function CounterDialog({
  title,
  sub,
  onClose,
  wide = false,
  foot,
  footNote,
  children,
}: {
  title: string
  sub?: string
  onClose: () => void
  wide?: boolean
  foot?: ReactNode
  footNote?: ReactNode
  children: ReactNode
}) {
  const { say } = useStaffText()
  return (
    <Modal.Backdrop isOpen onOpenChange={(open) => !open && onClose()}>
      <Modal.Container size={wide ? 'lg' : 'md'} scroll="inside">
        <Modal.Dialog className={wide ? 'cw-modal cw-modal--wide' : 'cw-modal'}>
          <Modal.CloseTrigger aria-label={say('ui.close')} />
          <Modal.Header>
            <Modal.Heading>{title}</Modal.Heading>
            {sub && <p className="cw-modal__sub">{sub}</p>}
          </Modal.Header>
          <Modal.Body className="cw-modal__body">{children}</Modal.Body>
          {(foot || footNote) && (
            <Modal.Footer className="cw-modal__foot">
              {footNote && <span className="cw-modal__note">{footNote}</span>}
              {foot}
            </Modal.Footer>
          )}
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}

/** A form about the customer at the desk, drawn inline under the dock. Escape closes it. */
export function CounterPanel({
  title,
  onClose,
  foot,
  children,
}: {
  title: string
  onClose: () => void
  foot?: ReactNode
  children: ReactNode
}) {
  const { say } = useStaffText()
  const ref = useRef<HTMLElement>(null)
  // The stage scrolls inside itself on a short window: bring the form and its buttons into view.
  useEffect(() => {
    const id = requestAnimationFrame(() => ref.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }))
    return () => cancelAnimationFrame(id)
  }, [])
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && !e.defaultPrevented) {
      e.preventDefault()
      onClose()
    }
  }
  return (
    <section ref={ref} className="cw-inline" aria-label={title} onKeyDown={onKeyDown}>
      <header className="cw-inline__head">
        <h3>{title}</h3>
        <CloseButton aria-label={say('ui.close')} onPress={onClose} />
      </header>
      {children}
      {foot && <div className="cw-inline__foot">{foot}</div>}
    </section>
  )
}

/** One option of a counter picker; `note` is the quiet second line. */
export type PickOption = { value: string; label: string; note?: string }

/** A HeroUI Select over plain options, value '' for nothing chosen. */
export function PickSelect({
  label,
  value,
  placeholder,
  disabled,
  options,
  autoFocus,
  onChange,
}: {
  label: string
  value: string
  placeholder: string
  disabled?: boolean
  options: readonly PickOption[]
  autoFocus?: boolean
  onChange: (value: string) => void
}) {
  return (
    <Select
      aria-label={label}
      className="cw-select"
      variant="secondary"
      fullWidth
      placeholder={placeholder}
      isDisabled={disabled}
      autoFocus={autoFocus}
      value={value || null}
      onChange={(v) => v != null && onChange(String(v))}
    >
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {options.map((o) => (
            <ListBox.Item key={o.value} id={o.value} textValue={o.label}>
              <span className="cw-select__item">
                <Label>{o.label}</Label>
                {o.note && <Description>{o.note}</Description>}
              </span>
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  )
}

/** A short text field with its label above. */
function Field({
  label,
  value,
  onChange,
  autoFocus,
  maxLength,
  placeholder,
  type,
}: {
  label: ReactNode
  value: string
  onChange: (value: string) => void
  autoFocus?: boolean
  maxLength?: number
  placeholder?: string
  type?: 'text' | 'search' | 'tel'
}) {
  return (
    <TextField className="cw-field" value={value} onChange={onChange} autoFocus={autoFocus} type={type} fullWidth>
      <Label>{label}</Label>
      <Input variant="secondary" maxLength={maxLength} placeholder={placeholder} autoComplete="off" />
    </TextField>
  )
}

/** One choice in Take a break: an organisation's break reason, or (from a server
 *  without reasons) a bare kind. */
export type BreakChoice = { id: string; label: string; kind: RequestKind; reasonId?: string }

/** None when the organisation has no reasons: the server refuses a bare kind then. */
export function breakChoices(reasons: readonly BreakReason[] | null, lang: StaffLanguage): BreakChoice[] {
  if (reasons) {
    return [...reasons]
      .sort((x, y) => x.sortOrder - y.sortOrder)
      .map((r) => ({ id: r.id, label: r.name, kind: r.kind, reasonId: r.id }))
  }
  return BREAK_KINDS.map((k) => ({ id: k, label: requestKindLabel(k, lang), kind: k }))
}

/** The menu item that opens Options when the organisation has no break reasons. */
const OPTIONS_ITEM = 'options'

/** Break: a HeroUI dropdown under the button. A reason starts the break (or asks, with
 *  approval on); every break runs until the clerk ends it. */
export function BreakMenu({
  choices,
  label,
  busy,
  onAsk,
  onOptions,
}: {
  choices: readonly BreakChoice[]
  /** The button's text, its busy text while a break is being asked. */
  label: string
  busy: boolean
  onAsk: (choice: BreakChoice) => void
  /** For whoever may set break reasons: opens Options. */
  onOptions?: () => void
}) {
  const { say } = useStaffText()
  return (
    <Dropdown>
      <Button variant="secondary" size="sm" isDisabled={busy}>
        <Icon name="pause" size={16} />
        {label}
        <Icon name="down" size={14} />
      </Button>
      <Dropdown.Popover placement="bottom end">
        <Dropdown.Menu
          aria-label={label}
          disabledKeys={choices.length === 0 ? ['none'] : busy ? choices.map((c) => c.id) : []}
          onAction={(key) => {
            if (key === OPTIONS_ITEM) {
              onOptions?.()
              return
            }
            const choice = choices.find((c) => c.id === key)
            if (choice) onAsk(choice)
          }}
        >
          {choices.length === 0 ? (
            <>
              <Dropdown.Item id="none" textValue={say('counter.noBreakReasons')}>
                {say('counter.noBreakReasons')}
              </Dropdown.Item>
              {onOptions ? (
                <Dropdown.Item id={OPTIONS_ITEM} textValue={say('org.opt.title')}>
                  {say('org.opt.title')}
                </Dropdown.Item>
              ) : null}
            </>
          ) : (
            choices.map((choice) => (
              <Dropdown.Item key={choice.id} id={choice.id} textValue={choice.label}>
                {choice.label}
              </Dropdown.Item>
            ))
          )}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  )
}

/** Calling somebody who is not the server's next: confirmed, with a reason. */
export function OutOfTurnDialog({
  ticket,
  reasonRequired,
  busy,
  onCall,
  onClose,
}: {
  ticket: Pick<Ticket, 'displayNumber'>
  reasonRequired: boolean
  busy: boolean
  onCall: (reason: string) => void
  onClose: () => void
}) {
  const { say } = useStaffText()
  const [reason, setReason] = useState('')
  const ok = !busy && (!reasonRequired || reason.trim() !== '')
  return (
    <CounterDialog
      title={say('counter.oot.title')}
      onClose={onClose}
      foot={
        <>
          <Button variant="tertiary" onPress={onClose}>{say('common.cancel')}</Button>
          <Button type="submit" form="counter-oot" isDisabled={!ok}>
            {say('counter.callNumber', { number: ticket.displayNumber })}
          </Button>
        </>
      }
    >
      <form
        id="counter-oot"
        className="cw-form"
        onSubmit={(e) => {
          e.preventDefault()
          if (ok) onCall(reason.trim())
        }}
      >
        <p className="cw-warning">{say('counter.oot.notNext', { number: ticket.displayNumber })}</p>
        <Field
          label={reasonRequired ? say('counter.reason') : say('counter.reasonOptional')}
          autoFocus
          maxLength={200}
          placeholder={say('counter.whyNow')}
          value={reason}
          onChange={setReason}
        />
      </form>
    </CounterDialog>
  )
}

/** Close counter, said before it happens: who goes back in line and who is held. */
export function CloseDialog({
  lines,
  deskName,
  busy,
  label,
  onConfirm,
  onClose,
}: {
  lines: string[]
  deskName: string
  busy: boolean
  label: string
  onConfirm: () => void
  onClose: () => void
}) {
  const { say } = useStaffText()
  return (
    <CounterDialog
      title={say('counter.closeQuestion')}
      onClose={onClose}
      foot={
        <>
          <Button variant="tertiary" onPress={onClose}>{say('common.cancel')}</Button>
          <Button autoFocus isDisabled={busy} onPress={onConfirm}>{label}</Button>
        </>
      }
    >
      <p className="cw-dialog__text">{lines.length > 0 ? lines.join(' ') : say('counter.closingEmpty', { desk: deskName })}</p>
    </CounterDialog>
  )
}

/** The desk's calling profile, switched while nobody is at the desk. `current` is
 *  empty on a desk opened on picked queues (simple mode). */
export function ProfileDialog({
  profiles,
  current,
  letters,
  names,
  busy,
  onSave,
  onClose,
}: {
  profiles: WorkProfile[]
  current: string
  letters: (p: WorkProfile) => string[]
  names: (p: WorkProfile) => string
  busy: boolean
  onSave: (workProfileId: string) => void
  onClose: () => void
}) {
  const { say } = useStaffText()
  const [chosen, setChosen] = useState(current)
  return (
    <CounterDialog
      title={say('counter.changeWork')}
      wide
      onClose={onClose}
      foot={
        <>
          <Button variant="tertiary" onPress={onClose}>{say('common.cancel')}</Button>
          <Button isDisabled={busy || !chosen || chosen === current} onPress={() => onSave(chosen)}>
            {say('common.save')}
          </Button>
        </>
      }
    >
      <ProfilePicker profiles={profiles} value={chosen} onChange={setChosen} letters={letters} names={names} />
    </CounterDialog>
  )
}

/** One choice of a segmented HeroUI toggle group, kept chosen (never empty). */
function Segments<T extends string>({
  label,
  value,
  items,
  disabled,
  onChange,
}: {
  label: string
  value: T
  items: readonly { id: T; label: string; title?: string }[]
  disabled?: boolean
  onChange: (value: T) => void
}) {
  return (
    <ToggleButtonGroup
      aria-label={label}
      size="sm"
      selectionMode="single"
      disallowEmptySelection
      isDisabled={disabled}
      selectedKeys={[value]}
      onSelectionChange={(keys) => {
        const next = [...keys][0]
        if (next !== undefined) onChange(String(next) as T)
      }}
    >
      {items.map((item, i) => (
        <ToggleButton key={item.id} id={item.id} aria-label={item.title ? `${item.label}, ${item.title}` : undefined}>
          {i > 0 && <ToggleButtonGroup.Separator />}
          <span title={item.title}>{item.label}</span>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

/** Hold: an optional reason, and where it draws from pools, who takes them back
 *  (Back to me first). */
export function AsidePanel({
  ticket,
  deskName,
  queueName,
  scopes,
  disabled,
  onCancel,
  onPark,
}: {
  ticket: Ticket
  deskName: string
  queueName: string
  scopes: ParkScope[]
  disabled: boolean
  onCancel: () => void
  onPark: (scope: ParkScope | undefined, reason: string) => void
}) {
  const { say } = useStaffText()
  const [why, setWhy] = useState('')
  const choices = scopes.filter((s): s is 'desk' | 'branch' => s === 'desk' || s === 'branch')
  const [scope, setScope] = useState<'desk' | 'branch' | undefined>(choices.includes('desk') ? 'desk' : choices[0])
  return (
    <CounterPanel
      title={say('counter.parkNumber', { number: ticket.displayNumber })}
      onClose={onCancel}
      foot={
        <>
          <Button variant="secondary" size="sm" type="submit" form="counter-hold" isDisabled={disabled}>
            <Icon name="pause" size={16} />
            {say('counter.park')}
          </Button>
          <Button variant="ghost" size="sm" onPress={onCancel}>{say('common.cancel')}</Button>
        </>
      }
    >
      <form
        id="counter-hold"
        className="cw-inline__row"
        onSubmit={(e) => {
          e.preventDefault()
          if (!disabled) onPark(scope, why)
        }}
      >
        <div className="cw-inline__grow">
          <Field label={say('counter.reasonOptional')} autoFocus maxLength={200} value={why} onChange={setWhy} />
        </div>
        {choices.length > 1 && scope && (
          <Segments
            label={say('counter.park')}
            value={scope}
            onChange={setScope}
            items={choices.map((c) => ({
              id: c,
              label: c === 'desk' ? say('counter.backToMe') : say('counter.anyDesk'),
              title: c === 'desk' ? say('counter.deskOnly', { desk: deskName }) : queueName,
            }))}
          />
        )}
      </form>
    </CounterPanel>
  )
}

// A transfer needs a destination, so choosing and sending are one panel. Three
// kinds of destination, each a different promise: another line (a transfer),
// another window or another person (a send, which is a park into their pool).
// Only the kinds this desk's profile allows are offered, each one dropdown.
export function TransferPanel({
  ticket,
  targets,
  services,
  queues,
  desks,
  colleagues,
  disabled,
  error,
  onCancel,
  onTransfer,
  onSend,
}: {
  ticket: Ticket
  targets: SendTarget[]
  services: Service[]
  queues: QueueView[]
  desks: CounterView[]
  colleagues: Colleague[]
  disabled: boolean
  /** The last refusal, shown with the panel it answers. */
  error?: string
  onCancel: () => void
  onTransfer: (serviceId: string, placed: Placed, returnToMe: boolean) => void
  onSend: (request: ReturnType<typeof sendRequest>) => void
}) {
  const { lang, say } = useStaffText()
  const note = useRef<NoteHistoryHandle>(null)
  const [saving, setSaving] = useState(false)
  const [dest, setDest] = useState<Dest | null>(null)
  const [placement, setPlacement] = useState<Placement>(COUNTER_DEFAULT_PLACEMENT)
  const [comeBack, setComeBack] = useState(false)
  const [tab, setTab] = useState<'queue' | 'counter'>(targets.includes('service') ? 'queue' : 'counter')

  // Services in the order of the queue they route into.
  const offerServices = targets.includes('service')
  const serviceOptions = useMemo(() => {
    const sorted = [...queues].sort((a, b) => a.sortOrder - b.sortOrder)
    const seen = new Set<string>()
    const out: PickOption[] = []
    const add = (s: Service, q?: QueueView) => {
      if (seen.has(s.id)) return
      seen.add(s.id)
      out.push({ value: `service:${s.id}`, label: nameIn(s.name, lang, s.code), note: q ? say('counter.nWaiting', { n: q.waiting }) : undefined })
    }
    for (const q of sorted) for (const s of services) if (q.serviceIds?.includes(s.id)) add(s, q)
    for (const s of services) add(s)
    return out
  }, [queues, services, lang, say])

  // Open desks first: they are the usual destination.
  const deskRows = targets.includes('desk') ? [...desks].sort((a, b) => Number(a.state === 'closed') - Number(b.state === 'closed')) : []
  const colleagueRows = targets.includes('colleague') ? colleagues : []
  const offerCounter = deskRows.length > 0 || colleagueRows.length > 0
  const counterOptions: PickOption[] = [
    ...deskRows.map((d) => ({
      value: `desk:${d.counterId}`,
      label: d.name,
      note: d.state === 'closed' ? say('common.desk.closed') : (d.staffName ?? undefined),
    })),
    ...colleagueRows.map((c) => ({ value: `colleague:${c.id}`, label: c.displayName, note: targetLabel('colleague', lang) })),
  ]

  const canSend = Boolean(dest) && !disabled && !saving

  // An unsent note is added first, while the visit is still at this desk.
  const send = async () => {
    if (!dest || disabled || saving) return
    setSaving(true)
    const saved = await (note.current?.save() ?? Promise.resolve(true))
    setSaving(false)
    if (!saved) return
    if (dest.kind === 'service') onTransfer(dest.id, placementBody(placement, 'service', 0), comeBack)
    else onSend(sendRequest(dest.kind, dest.id, placement, 0, comeBack))
  }

  const pick = (value: string) => {
    const at = value.indexOf(':')
    setDest({ kind: value.slice(0, at) as SendTarget, id: value.slice(at + 1) })
  }
  const switchTab = (to: 'queue' | 'counter') => {
    setTab(to)
    setDest(null)
  }

  return (
    <CounterPanel
      title={say('counter.transferNumber', { number: ticket.displayNumber })}
      onClose={onCancel}
      foot={
        <>
          <Button variant="secondary" size="sm" isDisabled={!canSend} isPending={saving} onPress={() => void send()}>
            <Icon name="swap" size={16} />
            {say('counter.send')}
          </Button>
          <Button variant="ghost" size="sm" onPress={onCancel}>{say('common.cancel')}</Button>
        </>
      }
    >
      {error && <p className="cw-inline__error" role="alert">{error}</p>}
      <div className="cw-inline__row">
        {offerServices && offerCounter && (
          <Segments
            label={say('counter.transfer')}
            value={tab}
            onChange={switchTab}
            items={[
              { id: 'queue', label: say('counter.toQueue'), title: say('counter.toQueueHint') },
              { id: 'counter', label: say('counter.toCounter'), title: say('counter.toCounterHint') },
            ]}
          />
        )}
        <div className="cw-inline__grow">
          <PickSelect
            key={tab}
            autoFocus
            label={say(tab === 'queue' ? 'counter.toQueue' : 'counter.toCounter')}
            value={dest ? `${dest.kind}:${dest.id}` : ''}
            placeholder={say(tab === 'queue' ? 'counter.chooseService' : 'counter.chooseCounter')}
            disabled={disabled}
            onChange={pick}
            options={tab === 'queue' ? serviceOptions : counterOptions}
          />
        </div>
        <div className="cw-inline__seg">
          <span className="cw-inline__label">{say('counter.whereTheyLand')}</span>
          <Segments
            label={say('counter.whereTheyLand')}
            value={placement}
            onChange={setPlacement}
            items={COUNTER_PLACEMENTS.map((p) => ({ id: p, label: placementLabel(p, lang) }))}
          />
        </div>
      </div>
      <Switch className="cw-switch" size="sm" isSelected={comeBack} isDisabled={disabled} onChange={setComeBack}>
        <Switch.Content>
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
          <Label title={say('counter.bringBackHint', { number: ticket.displayNumber })}>{say('counter.bringBack')}</Label>
        </Switch.Content>
      </Switch>
      <NoteHistory
        ticket={ticket}
        handle={note}
        list={false}
        label={
          <>
            {say('counter.noteNext')} <span className="ct-optional">{say('counter.optional')}</span>
          </>
        }
        placeholder={say('counter.noteNextHint')}
      />
    </CounterPanel>
  )
}

// Hand off: the shortcut over Transfer to a cashier service. Come back to me is
// the default; Finish there is a plain transfer. The services are listed even when
// there is one, so the clerk sees where the customer goes.
export function CashierPanel({
  title,
  cashiers,
  disabled,
  onCancel,
  onSend,
}: {
  /** The organisation's name for hand-off, or the phrase. */
  title: string
  cashiers: Service[]
  disabled: boolean
  onCancel: () => void
  onSend: (serviceId: string, returnToMe: boolean) => void
}) {
  const { lang, say } = useStaffText()
  const [serviceId, setServiceId] = useState(cashiers[0]?.id ?? '')
  const can = !disabled && Boolean(serviceId)

  return (
    <CounterPanel
      title={title}
      onClose={onCancel}
      foot={
        <>
          <Button variant="secondary" size="sm" autoFocus isDisabled={!can} onPress={() => onSend(serviceId, true)}>
            {say('counter.comeBack')}
          </Button>
          <Button variant="tertiary" size="sm" isDisabled={!can} onPress={() => onSend(serviceId, false)}>
            {say('counter.finishAtCashier')}
          </Button>
        </>
      }
    >
      {cashiers.length > 0 && (
        <div className="cw-chips">
          {cashiers.map((s) => (
            <ToggleButton
              key={s.id}
              size="sm"
              data-service={s.id}
              isSelected={s.id === serviceId}
              isDisabled={disabled}
              onChange={() => setServiceId(s.id)}
            >
              {nameIn(s.name, lang, s.code)}
            </ToggleButton>
          ))}
        </div>
      )}
    </CounterPanel>
  )
}

// One more service on a visit already under way. The cap and repeats are refused
// here as well as at the server, before the press the customer would watch fail.
export function PlanPanel({
  services,
  committed,
  already,
  disabled,
  onCancel,
  onAdd,
}: {
  services: Service[]
  /** How many services this visit already carries, the one in progress included. */
  committed: number
  already: string[]
  disabled: boolean
  onCancel: () => void
  onAdd: (serviceId: string) => Promise<boolean>
}) {
  const { lang, say } = useStaffText()
  const [serviceId, setServiceId] = useState('')
  const [refused, setRefused] = useState('')
  const full = committed >= MAX_PLANNED_SERVICES
  return (
    <CounterPanel title={say('counter.alsoNeeds')} onClose={onCancel}>
      <div className="cw-inline__row">
        <div className="cw-inline__grow">
          <PickSelect
            autoFocus
            label={say('counter.alsoNeeds')}
            value={serviceId}
            placeholder={say('counter.chooseService')}
            onChange={(value) => {
              setServiceId(value)
              setRefused('')
            }}
            disabled={full}
            options={services.filter((s) => !already.includes(s.id)).map((s) => ({ value: s.id, label: nameIn(s.name, lang, s.code) }))}
          />
        </div>
        <Button
          variant="secondary"
          size="sm"
          isDisabled={disabled || full || !serviceId}
          onPress={async () => {
            const why = whyNotPlanned(serviceId, committed, already, services, lang)
            if (why) {
              setRefused(why)
              return
            }
            setRefused('')
            if (await onAdd(serviceId)) setServiceId('')
          }}
        >
          {say('common.add')}
        </Button>
      </div>
      {(full || refused) && <p className="cw-inline__error">{full ? say('floor.planMax', { n: MAX_PLANNED_SERVICES }) : refused}</p>}
    </CounterPanel>
  )
}

// Somebody who walked up to a free desk without a ticket: served as an ordinary visit that starts now.
export function WalkIn({
  services,
  disabled,
  onCancel,
  onWalkIn,
}: {
  services: Service[]
  disabled: boolean
  onCancel: () => void
  onWalkIn: (serviceId: string) => Promise<boolean>
}) {
  const { lang, say } = useStaffText()
  const [serviceId, setServiceId] = useState('')
  return (
    <CounterPanel title={say('counter.serveWalkIn')} onClose={onCancel}>
      <div className="cw-inline__row">
        <div className="cw-inline__grow">
          <PickSelect
            autoFocus
            label={say('counter.serviceTheyCameFor')}
            value={serviceId}
            placeholder={say('counter.chooseService')}
            onChange={(value) => setServiceId(value)}
            options={services.map((s) => ({ value: s.id, label: nameIn(s.name, lang, s.code) }))}
          />
        </div>
        <Button variant="secondary" size="sm" isDisabled={disabled || !serviceId} onPress={() => void onWalkIn(serviceId)}>
          <Icon name="userPlus" size={16} />
          {say('counter.serve')}
        </Button>
        <Button variant="ghost" size="sm" onPress={onCancel}>{say('common.cancel')}</Button>
      </div>
    </CounterPanel>
  )
}

// What the desk delivered within the piece of work in front of it. Taking one back
// asks nothing: the customer is still there, and it is added again in two presses.
export function DeliveredPanel({
  offered,
  entries,
  disabled,
  onAdd,
  onRemove,
}: {
  offered: DeliveredService[]
  entries: DeliveredEntry[]
  disabled: boolean
  onAdd: (deliveredServiceId: string, outcomeCode: string) => Promise<boolean>
  onRemove: (entryId: string) => void
}) {
  const { lang, say } = useStaffText()
  return (
    <div className="counter-delivered">
      <span className="counter-row__label">{say('counter.delivered')}</span>
      {entries.map((entry) => (
        <div key={entry.id} className="counter-delivered__row">
          <span className="muted">{entryTime(entry)}</span>
          <span>{entryLabel(entry, offered, lang)}</span>
          <Button variant="ghost" size="sm" isDisabled={disabled} onPress={() => onRemove(entry.id)}>
            {say('common.remove')}
          </Button>
        </div>
      ))}
      {offered.length > 0 && <DeliveredPicker offered={offered} disabled={disabled} onPick={onAdd} />}
    </div>
  )
}

// Type to find a delivered service, press it to add it; one with outcomes asks how
// it ended first. The search box keeps focus between presses, so a clerk adds
// three things without reaching for the mouse.
function DeliveredPicker({
  offered,
  disabled,
  onPick,
}: {
  offered: DeliveredService[]
  disabled: boolean
  onPick: (deliveredServiceId: string, outcomeCode: string) => unknown
}) {
  const { lang, say } = useStaffText()
  const [query, setQuery] = useState('')
  const [asking, setAsking] = useState<DeliveredService | null>(null)
  const shown = filterDelivered(offered, query)

  const pick = async (deliveredServiceId: string, outcomeCode: string) => {
    const ok = await onPick(deliveredServiceId, outcomeCode)
    // Kept on a refusal, so the clerk can read why without typing again.
    if (ok !== false) {
      setAsking(null)
      setQuery('')
    }
  }

  if (asking) {
    return (
      <div className="counter-row">
        <span className="counter-hint">{say('counter.howDidItEnd', { name: deliveredName(asking, lang) })}</span>
        {offeredOutcomes(asking).map((o) => (
          <Button key={o.id} variant="tertiary" size="sm" isDisabled={disabled} onPress={() => void pick(asking.id, o.code)}>
            {deliveredName(o, lang)}
          </Button>
        ))}
        <Button variant="ghost" size="sm" onPress={() => setAsking(null)}>{say('common.cancel')}</Button>
      </div>
    )
  }
  return (
    <>
      <Input
        variant="secondary"
        fullWidth
        aria-label={say('counter.findDelivered')}
        placeholder={say('counter.addDelivered')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="counter-row">
        {shown.map((d) => (
          <Button key={d.id} variant="tertiary" size="sm" isDisabled={disabled} onPress={() => (needsOutcome(d) ? setAsking(d) : void pick(d.id, ''))}>
            {deliveredName(d, lang)}
          </Button>
        ))}
        {shown.length === 0 && <span className="counter-hint">{say('counter.nothingMatches')}</span>}
      </div>
    </>
  )
}
